<template>
  <div>
    <div class="card bg-primary text-white mb-4">
      <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.Step2.Title') }}</h5>
    </div>

    <!-- Informationen zum Ergebnisbericht -->
    <b-card class="mb-4">
      <h5 class="card-title mb-4">
        <font-awesome-icon :icon="['far', 'file-alt']" class="mr-2" />
        {{ $t('Webportal.Actions.InquiryWizard.Step2.CheckInfo') }}
      </h5>

      <!-- Loading State -->
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px">
        <div class="text-center">
          <b-spinner variant="primary" :label="$t('Webportal.Loading')"></b-spinner>
          <p class="mt-3 text-muted">{{ $t('Webportal.Loading') }}...</p>
        </div>
      </div>

      <template v-else-if="surveyInstanceData">
        <div class="row mb-3">
          <div class="col-md-4">
            <label class="text-muted small">{{ $t('Webportal.Actions.InquiryWizard.Step2.CheckId') }}</label>
            <div class="font-weight-bold">{{ surveyInstanceData.SurveyInstance.ExternalSurveyInstanceId }}</div>
          </div>
          <div class="col-md-4">
            <label class="text-muted small">{{ $t("Webportal.Survey") }}</label>
            <div class="font-weight-bold">
              {{ surveyInstanceData.SurveyInstance.Survey.Title }}
            </div>
          </div>
          <div v-if="hasInquiryWizardSurveyInstanceStatus()" class="col-md-4">
            <label class="text-muted small">{{ $t("Webportal.Status") }}</label>
            <div>
              <b-badge variant="primary" class="status-badge">
                <span>
                  <font-awesome-icon :icon="['far', getSurveyInstanceStatusIcon(surveyInstanceData.SurveyInstance.SurveyInstanceStatusId)]" />
                  {{ getSurveyInstanceStatusText(surveyInstanceData.SurveyInstance.SurveyInstanceStatusId) }}
                </span>
              </b-badge>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <label class="text-muted small">{{ $t("Webportal.Customer") }}</label>
            <div class="font-weight-bold">{{ surveyInstanceData.SurveyInstance.Client.Name }}</div>
          </div>
          <div v-if="isMsmUser || surveyInstanceData.SurveyInstance.ShowCheckDate" class="col-md-4">
            <label class="text-muted small">{{ $t('Webportal.Actions.InquiryWizard.Step2.VisitDate') }}</label>
            <div class="font-weight-bold">{{ surveyInstanceData.SurveyInstance.TimeStampSurveyStart | msm_date }}</div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <label class="text-muted small">{{ $t('Webportal.Actions.InquiryWizard.Step2.OverallResult') }}</label>
            <div>
              {{ surveyInstanceData.SurveyInstance.Result != null ? $n(surveyInstanceData.SurveyInstance.Result * 100) + "%" : "" }}
              ({{ surveyInstanceData.SurveyInstance.PointsReached }}/{{ surveyInstanceData.SurveyInstance.PointsMax }})
              <b-badge v-if="thresholdForResult" class="result-badge ml-1" :style="{ backgroundColor: thresholdForResult.Color }">
                <font-awesome-icon v-if="thresholdForResult.Smiley" :icon="['fal', thresholdForResult.Smiley]" size="lg" />
                <span v-else>&nbsp;</span>
              </b-badge>
            </div>
          </div>
          <div class="col-md-4">
            <label class="text-muted small">{{ $t("Webportal.LocationId") }}</label>
            <div class="font-weight-bold">{{ surveyInstanceData.SurveyInstance.ClientLocation.ClientLocationIdOnClientSite }}</div>
          </div>
          <div class="col-md-4">
            <label class="text-muted small">{{ $t("Webportal.LocationAddress") }}</label>
            <div class="font-weight-bold">
              {{ surveyInstanceData.SurveyInstance.ClientLocation.Name }}
              <template v-if="surveyInstanceData.SurveyInstance.ClientLocation.Address">
                <br />{{ surveyInstanceData.SurveyInstance.ClientLocation.Address }}
              </template>
              <template v-if="surveyInstanceData.SurveyInstance.ClientLocation.Address2">
                <br />{{ surveyInstanceData.SurveyInstance.ClientLocation.Address2 }}
              </template>
              <br />{{ surveyInstanceData.SurveyInstance.ClientLocation.City }},
              {{ surveyInstanceData.SurveyInstance.ClientLocation.CountryId }} -
              {{ surveyInstanceData.SurveyInstance.ClientLocation.PostalCode }}
            </div>
          </div>
        </div>
      </template>
    </b-card>

    <!-- Historie des Checks -->
    <b-card class="mb-4">
      <h5 class="card-title mb-4">
        <font-awesome-icon :icon="['far', 'history']" class="mr-2" />
        {{ $t('Webportal.Actions.InquiryWizard.Step2.History') }}
      </h5>

      <!-- Loading history -->
      <div v-if="isLoadingHistory" class="d-flex justify-content-center align-items-center" style="min-height: 150px">
        <div class="text-center">
          <b-spinner variant="primary" :label="$t('Webportal.Loading')"></b-spinner>
          <p class="mt-3 text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step2.HistoryLoading') }}</p>
        </div>
      </div>

      <!-- History timeline -->
      <div v-else-if="historyEvents && historyEvents.length > 0">
        <div v-for="(historyEvent, index) in historyEvents" :key="index" class="row mb-3">
          <div class="col-auto align-self-start pr-0">
            <div v-b-tooltip.hover.nofade="toolTipDelay" :title="getActorName(historyEvent)">
              <font-awesome-layers full-width class="fa-2x text-secondary">
                <font-awesome-icon icon="circle" />
                <font-awesome-layers-text
                  v-if="historyEvent.Actor"
                  class="col-form-label-sm btn-secondary"
                  style="background-color: transparent"
                  :value="getUserInitials(historyEvent.Actor.FirstName, historyEvent.Actor.LastName)"
                />
              </font-awesome-layers>
            </div>
          </div>
          <div class="col">
            <div class="row align-items-center">
              <div class="col">
                <p class="mb-0">{{ getHistoryEventTypeLabel(historyEvent) }}</p>
              </div>
              <div class="col-auto text-right">
                <span class="col-form-label-sm text-muted">
                  {{ historyEvent.EventTimestamp | msm_datetime_long }}
                </span>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <span class="col-form-label-sm text-muted">
                  {{ getHistoryEventTypeDescription(historyEvent) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No history -->
      <div v-else class="text-muted">
        <font-awesome-icon :icon="['far', 'info-circle']" class="mr-2" />
        {{ $t('Webportal.Actions.InquiryWizard.Step2.NoHistory') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import { mapGetters } from "vuex";
import ActionService from "@/services/ActionService";
import AuthenticationService from '@/services/AuthenticationService';
import DashboardService from "@/services/DashboardService-APP_TARGET";
import ThresholdColor from "@/mixins/ThresholdColor";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import SurveyInstanceMixin from "@/mixins/SurveyInstanceMixin";

export default {
  name: "Step2Overview",

  mixins: [AppConfig, ThresholdColor, SurveyInstanceMixin, SurveyRevisionMixin],

  data() {
    return {
      isLoading: false,
      isLoadingHistory: false,
      surveyInstanceData: null,
      historyEvents: null,
      thresholds: null,
    };
  },

  computed: {
    ...mapGetters("inquiryWizard", ["selectedCheckId"]),

    isMsmUser() {
      return AuthenticationService.isMSMUser;
    },

    thresholdForResult() {
      if (!this.thresholds || !this.surveyInstanceData?.SurveyInstance?.Result) {
        return null;
      }

      const value = this.surveyInstanceData.SurveyInstance.Result * 100;

      return this.getThreshold(this.thresholds, value);
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      if (!this.selectedCheckId) {
        // Redirect to landing page when no check is selected
        this.$router.push("/action/inquiry-wizard");
        return;
      }

      this.isLoading = true;
      this.isLoadingHistory = true;

      try {
        // Load all data in parallel
        const [surveyResponse, historyResponse] = await Promise.all([
          ActionService.getSurveyInstanceData(this.selectedCheckId),
          ActionService.getSurveyInstanceHistory(this.selectedCheckId),
        ]);

        this.surveyInstanceData = surveyResponse.data;
        this.historyEvents = historyResponse.data;

        // Load thresholds for overall score color
        const clientId = this.surveyInstanceData.SurveyInstance.Client.ClientId;
        const thresholdsResponse = await DashboardService.getThresholds(clientId, -1);
        this.thresholds = thresholdsResponse.data;

        this.isLoading = false;
        this.isLoadingHistory = false;
      } catch (error) {
        console.error("Error loading data:", error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step2.LoadError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
        this.isLoading = false;
        this.isLoadingHistory = false;
      }
    },

    getActorName(historyEvent) {
      if (!historyEvent.Actor || !historyEvent.Actor.FirstName) {
        return null;
      }
      const firstName = historyEvent.Actor.FirstName || "";
      const lastName = historyEvent.Actor.LastName || "";
      const email = historyEvent.Actor.Email ? ` (${historyEvent.Actor.Email})` : "";
      
      return `${firstName} ${lastName}${email}`.trim();
    },
  },
};
</script>

<style lang="scss" scoped>
.card-title {
  font-size: 1.1rem;
  font-weight: 600;
}

label.small {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.status-badge {
  font-size: 90%;
}

.result-badge {
  width: 30px;
  color: black;
}
</style>
